import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { Table, Button, Spin } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { createSimpleStore } from 'react-simple-reducer'
import Axios from 'axios'
import { subMonths, startOfMonth, format } from 'date-fns'

interface ParceirosRow {
  month: string
  new_partners: number
  total_enrollments: number
  total_revenue: number
  median_ticket: number
}

interface DetalheRow {
  parceiro_id: number
  nome: string
  uf: string
  created_at: string
  total_enrollments: number
  total_revenue: number
  median_ticket: number
}

const MONTHS_PER_PAGE = 12

const BiParceirosStore = createSimpleStore(
  {
    startDate: twelveMonthsAgo(),
    loading: false as boolean,
    loadingMore: false as boolean,
    rows: [] as ParceirosRow[],
    expandedMonths: {} as Record<string, DetalheRow[] | 'loading'>,
    error: null as string | null,
  },
  {
    fetchStarted: (state) => ({ ...state, loading: true, error: null }),
    fetchSuccess: (state, rows: ParceirosRow[]) => ({ ...state, rows, loading: false }),
    fetchFailed: (state, error: string) => ({ ...state, loading: false, error }),
    loadMoreStarted: (state) => ({ ...state, loadingMore: true }),
    loadMoreSuccess: (state, { startDate, rows }: { startDate: string; rows: ParceirosRow[] }) => ({
      ...state,
      startDate,
      rows,
      loadingMore: false,
    }),
    loadMoreFailed: (state) => ({ ...state, loadingMore: false }),
    detalheLoading: (state, month: string) => ({
      ...state,
      expandedMonths: { ...state.expandedMonths, [month]: 'loading' as const },
    }),
    detalheLoaded: (state, { month, rows }: { month: string; rows: DetalheRow[] }) => ({
      ...state,
      expandedMonths: { ...state.expandedMonths, [month]: rows },
    }),
    rowCollapsed: (state, month: string) => {
      const { [month]: _removed, ...rest } = state.expandedMonths
      return { ...state, expandedMonths: rest }
    },
  },
  {
    thunks: {
      fetchData(startDate: string) {
        return async (dispatch: any) => {
          dispatch(BiParceirosStore.actions.fetchStarted())
          try {
            const { data } = await Axios.get('/matriculas/bi/parceiros', { params: { startDate } })
            dispatch(BiParceirosStore.actions.fetchSuccess(data))
          } catch {
            dispatch(BiParceirosStore.actions.fetchFailed('Erro ao carregar dados'))
          }
        }
      },
      loadMore(currentStartDate: string) {
        return async (dispatch: any) => {
          dispatch(BiParceirosStore.actions.loadMoreStarted())
          const newStartDate = format(
            startOfMonth(subMonths(new Date(`${currentStartDate}-01`), MONTHS_PER_PAGE)),
            'yyyy-MM'
          )
          try {
            const { data } = await Axios.get('/matriculas/bi/parceiros', {
              params: { startDate: newStartDate },
            })
            dispatch(BiParceirosStore.actions.loadMoreSuccess({ startDate: newStartDate, rows: data }))
          } catch {
            dispatch(BiParceirosStore.actions.loadMoreFailed())
          }
        }
      },
      fetchDetalhe(month: string) {
        return async (dispatch: any) => {
          dispatch(BiParceirosStore.actions.detalheLoading(month))
          try {
            const { data } = await Axios.get('/matriculas/bi/parceiros/detalhe', {
              params: { month },
            })
            dispatch(BiParceirosStore.actions.detalheLoaded({ month, rows: data }))
          } catch {
            dispatch(BiParceirosStore.actions.detalheLoaded({ month, rows: [] }))
          }
        }
      },
    },
  }
)

export function BiAquisicaoParceiros() {
  const init = useCallback((dispatch: any) => {
    dispatch(BiParceirosStore.thunks.fetchData(twelveMonthsAgo()))
  }, [])

  return (
    <BiParceirosStore.Provider init={init}>
      <ParceirosTable />
    </BiParceirosStore.Provider>
  )
}

function ParceirosTable() {
  const { rows, loading, loadingMore, startDate, expandedMonths } = BiParceirosStore.useState()
  const dispatch = BiParceirosStore.useDispatch()

  const columns: ColumnsType<ParceirosRow> = [
    {
      title: 'Mês',
      dataIndex: 'month',
      render: (month: string) => formatMonth(month),
    },
    {
      title: 'Novos Parceiros',
      dataIndex: 'new_partners',
    },
    {
      title: 'Matrículas Totais',
      dataIndex: 'total_enrollments',
    },
    {
      title: 'Receita NewDriver',
      dataIndex: 'total_revenue',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Ticket Mediano',
      dataIndex: 'median_ticket',
      render: (value: number) => (value > 0 ? formatCurrency(value) : '—'),
    },
  ]

  function handleExpand(expanded: boolean, record: ParceirosRow) {
    if (!expanded) {
      dispatch(BiParceirosStore.actions.rowCollapsed(record.month))
      return
    }
    if (!expandedMonths[record.month]) {
      dispatch(BiParceirosStore.thunks.fetchDetalhe(record.month))
    }
  }

  return (
    <div>
      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="month"
        pagination={false}
        expandable={{
          expandedRowKeys: Object.keys(expandedMonths),
          onExpand: handleExpand,
          expandedRowRender: (record) => (
            <DetalheTable month={record.month} expandedMonths={expandedMonths} />
          ),
        }}
      />
      <LoadMoreRow>
        <Button onClick={() => dispatch(BiParceirosStore.thunks.loadMore(startDate))} loading={loadingMore}>
          Carregar mais
        </Button>
      </LoadMoreRow>
    </div>
  )
}

function DetalheTable({
  month,
  expandedMonths,
}: {
  month: string
  expandedMonths: Record<string, DetalheRow[] | 'loading'>
}) {
  const detalhe = expandedMonths[month]

  if (detalhe === 'loading') {
    return <SpinWrapper><Spin /></SpinWrapper>
  }

  const rows = detalhe ?? []

  const columns: ColumnsType<DetalheRow> = [
    {
      title: 'Parceiro',
      dataIndex: 'nome',
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      render: (uf: string) => uf ?? '—',
    },
    {
      title: 'Data Cadastro',
      dataIndex: 'created_at',
      render: (date: string) => new Date(date + 'T00:00:00').toLocaleDateString('pt-BR'),
    },
    {
      title: 'Matrículas Totais',
      dataIndex: 'total_enrollments',
    },
    {
      title: 'Receita NewDriver',
      dataIndex: 'total_revenue',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Ticket Mediano',
      dataIndex: 'median_ticket',
      render: (value: number) => (value > 0 ? formatCurrency(value) : '—'),
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey="parceiro_id"
      pagination={false}
      size="small"
    />
  )
}

function twelveMonthsAgo(): string {
  return format(startOfMonth(subMonths(new Date(), MONTHS_PER_PAGE)), 'yyyy-MM')
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-')
  const date = new Date(Number(year), Number(m) - 1, 1)
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`
